import mapboxgl from "mapbox-gl";
import * as geojson from "geojson";
import { useLayerStale } from "../../hooks/useLayerStale";
import { Layer } from "../../types/layer";
import { ImageryOption } from "../../types/dataReview";

/** The photo source the user has picked, as map tiles, so the two sources
 *  can be compared before a run. One slot: the map only updates layers it
 *  is handed and never removes missing ones, so with nothing to show the
 *  slot keeps its last tiles, switched off. A changed URL rebuilds it. */
export const previewTilesUrl = (option: ImageryOption | null | undefined): string | null =>
  option?.preview_tiles ? option.preview_tiles.replace("{token}", mapboxgl.accessToken ?? "") : null;

/** Tiles are only fetched over the traced area (padded a little), which is
 *  both cheaper and what the user is judging. */
const boundsOf = (area: geojson.Feature | null | undefined): [number, number, number, number] | undefined => {
  const g = area?.geometry;
  const rings: geojson.Position[][] =
    g?.type === "Polygon" ? g.coordinates : g?.type === "MultiPolygon" ? g.coordinates.flatMap((p) => p) : [];
  const pts = rings.flat();
  if (!pts.length) return undefined;
  const xs = pts.map((p) => p[0]); const ys = pts.map((p) => p[1]);
  const pad = 0.002;
  return [Math.min(...xs) - pad, Math.min(...ys) - pad, Math.max(...xs) + pad, Math.max(...ys) + pad];
};

const DataReviewImageryPreviewLayer = (
  id: string, option: ImageryOption | null | undefined, visible: boolean, area?: geojson.Feature | null,
): Layer => {
  const url = previewTilesUrl(option);
  const bounds = boundsOf(area);
  const staleOf = useLayerStale();
  const source = {
    tiles: [url ?? "https://invalid.local/{z}/{x}/{y}"],
    tileSize: option?.preview_tile_px ?? 256,
    minzoom: 0,
    maxzoom: 21,
    ...(bounds ? { bounds } : {}),
  };
  const styleKey = `${source.tiles[0]}|${bounds ? bounds.map((v) => v.toFixed(4)).join(",") : ""}`;
  return {
    id,
    name: "Aerial photo",
    type: "raster",
    source,
    layerStyle: { id, type: "raster", source, paint: { "raster-opacity": 1, "raster-fade-duration": 0 } },
    styleKey,
    toolbarButton: null,
    active: visible && !!url,
    ...staleOf(id, styleKey, source.tileSize),
  };
};

export default DataReviewImageryPreviewLayer;
